import { getApi } from "../Services/apiService";
import { getDashboardMemberNameParts } from "./dashboardMemberDisplayName";
import { getMemberBirthCalendarParts } from "./family-tree/dateAndTime";

/**
 * "Family Members (since 2000)" — GET
 * /report-management/family-members-by-year-of-birth (rpt_NewFamSince2000).
 *
 * The endpoint does the whole report: descendants and their living spouses,
 * grouped by the year they were born in, oldest year first, each row carrying
 * its date of birth, family-line colour and the head of that line. The
 * deceased and the divorced are already left out. `start_year` is what makes it
 * "since 2000"; without it the endpoint returns every year the tree holds.
 *
 * Both the dashboard card's count and the detail page's list come through here,
 * so the two can never show different numbers. The Reports page reads the same
 * endpoint directly (see reports.jsx) — it lays the report out on its own.
 *
 * Sample row:
 *   { id, name: "Tyrese", first_name, middle_name, last_name, suffix,
 *     special_name, dob: "2000-04-12", color_name: "Red", hex_code: "#FF0000",
 *     generation_level: 3, head_first_name, head_last_name, head_suffix,
 *     head_special_name, is_spouse: false }
 */

/** The first birth year the report asks for — "Since 2000 - to current". */
export const FAMILY_MEMBERS_SINCE_YEAR = 2000;


const EMPTY_REPORT = {
  startYear: FAMILY_MEMBERS_SINCE_YEAR,
  endYear: null,
  totalCount: 0,
  years: [],
};

/** Whole number or null — the report prints "—" for anything it cannot read. */
const toNumberOrNull = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

/** The endpoint flags its in-laws is_spouse; the roster says is_in_laws. */
export const isFamilyMembersByYearSpouse = (member) =>
  member?.is_spouse === true ||
  member?.is_spouse === "true" ||
  member?.is_spouse === 1;

/** One API row → the columns the report prints. */
function toReportRow(member, year, index) {
  // The name as every other dashboard list spells it — "Ted, Sr.",
  // "Josephine M", and in-laws with their last name ("Lisa Witherspoon").
  // The endpoint flags its in-laws is_spouse, so that is read as is_in_laws.
  // The "(spouse)" parenthetical is left off: the rows are keyed back to
  // their roster record for the markers anyway.
  const { baseName } = getDashboardMemberNameParts(
    isFamilyMembersByYearSpouse(member) ? { ...member, is_in_laws: true } : member,
    {
      unnamedLabel: "",
      strictHeadForInLaw: true,
      appendDescendantSpouse: false,
    }
  );

  const birth = getMemberBirthCalendarParts(member);
  const name = baseName || String(member?.name ?? "").trim();

  return {
    id: String(member?.id ?? `family-member-${year}-${index}`),
    member,
    year,
    // The date of birth is not printed — it is what orders the rows inside a
    // year, so a year reads January through December.
    birthMonth: birth ? birth.month + 1 : null,
    birthDay: birth ? birth.day : null,
    baseName: name,
    descendantSpouseName: "",
    // What the A-Z sort reads, so it sorts on the name as printed.
    sortName: name,
    isSpouse: isFamilyMembersByYearSpouse(member),
    colorName: String(member?.color_name ?? "").trim(),
    hexCode: String(member?.hex_code ?? "").trim() || "#000000",
  };
}

/** Calendar order inside a year, so a year reads January through December. */
const byBirthDate = (a, b) =>
  (a.birthMonth ?? 13) - (b.birthMonth ?? 13) ||
  (a.birthDay ?? 32) - (b.birthDay ?? 32) ||
  a.sortName.localeCompare(b.sortName);

/**
 * The year blocks the report reads as, plus the grand total the heading and
 * the dashboard card print. `totalCount` is the API's own where it names one.
 */
export async function fetchFamilyMembersByYearOfBirth(
  familyTreeId,
  { startYear = FAMILY_MEMBERS_SINCE_YEAR } = {}
) {
  if (!familyTreeId) return EMPTY_REPORT;

  const response = await getApi(
    `/report-management/family-members-by-year-of-birth?family_tree_id=${encodeURIComponent(
      familyTreeId
    )}&start_year=${startYear}`
  );

  if (response?.statusCode !== 200 || !response?.data) return EMPTY_REPORT;

  const data = response.data;
  const years = (Array.isArray(data.years) ? data.years : [])
    .map((group) => {
      const year = toNumberOrNull(group?.year);
      const rows = (Array.isArray(group?.members) ? group.members : [])
        .map((member, index) => toReportRow(member, year, index))
        .sort(byBirthDate);
      return { key: year === null ? "unknown" : String(year), year, rows };
    })
    // Oldest year first, as the endpoint sends them; a year with nobody in it
    // is not a heading worth printing.
    .filter((group) => group.rows.length > 0);

  const rowCount = years.reduce((sum, group) => sum + group.rows.length, 0);

  return {
    startYear: toNumberOrNull(data.start_year) ?? startYear,
    endYear: toNumberOrNull(data.end_year),
    totalCount: toNumberOrNull(data.total_members) ?? rowCount,
    years,
  };
}

/**
 * The page as a flat run of blocks, for the two-column split: each year's
 * heading, then one block per row under it. The split is measured block by
 * block, so a year can break across the columns row by row. Same shape the
 * state report splits on — see toDescByStateBlocks.
 */
export function toFamilyMembersByYearBlocks(years) {
  const blocks = [];

  years.forEach((group) => {
    blocks.push({ kind: "heading", key: `heading-${group.key}`, group });
    group.rows.forEach((row) => {
      blocks.push({ kind: "row", key: `row-${group.key}-${row.id}`, group, row });
    });
  });

  return blocks;
}

/**
 * The blocks cut into two columns at `splitAt` — the first `splitAt` blocks,
 * then the rest — the way the page reads them: the cut never leaves a year's
 * heading as the last thing in the first column, and where it falls inside a
 * year the second column opens with that year again, marked `continued`, so
 * the rows there still read under their year. A cut at or past either end
 * comes back as one column, the whole page.
 */
export function splitFamilyMembersByYearBlocks(blocks, splitAt) {
  let cut = splitAt;
  if (blocks[cut - 1]?.kind === "heading") cut -= 1;
  if (cut <= 0 || cut >= blocks.length) return [blocks];

  const first = blocks.slice(0, cut);
  const second = blocks.slice(cut);
  if (second[0].kind === "row") {
    const { group } = second[0];
    second.unshift({
      kind: "heading",
      key: `heading-${group.key}-continued`,
      group,
      continued: true,
    });
  }

  return [first, second];
}
